// import axios from "axios";
// import { API_BASE_URL } from "../../constants/main";


// const baseURL = API_BASE_URL || "";

// const userToken = JSON.parse(localStorage.getItem("user") || "{}")?.token;

// const axiosInstance = axios.create({
//   baseURL,
//   headers: {
//     common: {
//       Authorization: `Bearer ${userToken}`,
//     },
//   },
// });


// function setAuthToken(token = "") {
//   axiosInstance.defaults.headers.common.Authorization = `Bearer ${token}`;
// }

// const methods = {
//   get: axiosInstance.get,
//   post: axiosInstance.post,
//   put: axiosInstance.put,
//   delete: axiosInstance.delete,
//   patch: axiosInstance.patch,
//   setAuthToken,
// };

// export default methods;


import axios from "axios";
import { API_BASE_URL } from "../../constants/main";
import { ROLE } from "../../utils/common";
import {
  endSessionAndRedirect,
  isAuthExemptRequest,
  isOnLoginPage,
  isPermissionDeniedResponse,
  LOGIN_REASONS,
  shouldEndSessionForAuthError,
} from "../../utils/sessionAuth";
import { isImpersonationSession, notifyImpersonationForbidden } from "../../utils/impersonation";

const baseURL = API_BASE_URL || "";

const getUserDetails = () => JSON.parse(localStorage.getItem("user") || "{}");
const RATE_LIMIT_COOLDOWN_KEY = "api-rate-limit-until";
const DEFAULT_RATE_LIMIT_BACKOFF_MS = 30_000;
const MAX_RATE_LIMIT_BACKOFF_MS = 15 * 60 * 1000;
/** Cap post-login admin cooldown so one shared-IP burst does not block the portal for 15 minutes. */
const NON_LOGIN_RATE_LIMIT_BACKOFF_MAX_MS = 60_000;

/** Paths exempt from client-side admin cooldown (mirrors server adminRoutes apiLimiter skip list). */
const ADMIN_CLIENT_COOLDOWN_EXEMPT_PATHS = ["/admin/ai-terms"];

const isAdminClientCooldownExempt = (url: string | undefined): boolean =>
  Boolean(url && ADMIN_CLIENT_COOLDOWN_EXEMPT_PATHS.some((path) => url.includes(path)));

let localRateLimitUntil = 0;
let hasLoggedRateLimit = false;

const readRateLimitUntilFromStorage = (): number => {
  const value = localStorage.getItem(RATE_LIMIT_COOLDOWN_KEY);
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const syncRateLimitUntil = (candidateUntil: number) => {
  const boundedUntil = Math.min(
    Date.now() + MAX_RATE_LIMIT_BACKOFF_MS,
    Math.max(candidateUntil, Date.now())
  );
  localRateLimitUntil = Math.max(localRateLimitUntil, boundedUntil);
  localStorage.setItem(RATE_LIMIT_COOLDOWN_KEY, String(localRateLimitUntil));
};

const getRetryAfterMs = (error: any): number => {
  const retryAfterHeader = error?.response?.headers?.["retry-after"] ?? error?.response?.headers?.["Retry-After"];
  if (!retryAfterHeader) return DEFAULT_RATE_LIMIT_BACKOFF_MS;

  const asNumber = Number(retryAfterHeader);
  if (Number.isFinite(asNumber) && asNumber > 0) {
    return Math.min(asNumber * 1000, MAX_RATE_LIMIT_BACKOFF_MS);
  }

  const asDate = Date.parse(String(retryAfterHeader));
  if (!Number.isNaN(asDate)) {
    return Math.min(Math.max(asDate - Date.now(), 1000), MAX_RATE_LIMIT_BACKOFF_MS);
  }

  return DEFAULT_RATE_LIMIT_BACKOFF_MS;
};

if (typeof window !== "undefined") {
  localRateLimitUntil = readRateLimitUntilFromStorage();
  window.addEventListener("storage", (event: StorageEvent) => {
    if (event.key === RATE_LIMIT_COOLDOWN_KEY) {
      const until = Number(event.newValue);
      if (Number.isFinite(until)) {
        localRateLimitUntil = Math.max(localRateLimitUntil, until);
      }
    }
  });
}

const axiosInstance = axios.create({
  baseURL,
  // Add timeout to prevent hanging requests
  timeout: 30000,
});

function hasPermission(url: any, method: any) {
  const userDetails = getUserDetails();
  const role = userDetails?.user?.role;
  const isStandardUser = role === ROLE.STANDARD_USER;

  if (isStandardUser) {
    if (url.includes('/admin/target/targets')) return false;
    if (url.includes('/admin/groups')) return false;
    if (url.includes('/admin/entity')) return false;
    if (url.includes('/admin/entity-user-tags')) return false;
    if (url.includes('/admin/assessments/assessments')) return false;
  }

  return true;
}

// Add request logging for debugging
axiosInstance.interceptors.request.use(
  (config) => {
    const { url, method } = config;
    const now = Date.now();

    localRateLimitUntil = Math.max(localRateLimitUntil, readRateLimitUntilFromStorage());
    if (localRateLimitUntil > 0 && now >= localRateLimitUntil) {
      localRateLimitUntil = 0;
      hasLoggedRateLimit = false;
      localStorage.removeItem(RATE_LIMIT_COOLDOWN_KEY);
    }
    
    // Dynamically set Authorization header from localStorage on each request
    const userDetails = getUserDetails();
    if (userDetails?.token) {
      const token = String(userDetails.token);
      // nginx returns 400 without CORS headers when Authorization exceeds ~8KB
      if (token.length > 7500) {
        localStorage.removeItem("user");
        localStorage.removeItem("oldUser");
        return Promise.reject({
          message: "Session token is too large. Please log in again.",
          status: 401,
          isOversizedToken: true,
        });
      }
      config.headers.Authorization = `Bearer ${token}`;
    }

    // During server-enforced cooldown, short-circuit admin requests client-side
    // to avoid creating a self-sustaining 429 storm. AI terms stay reachable so
    // the post-login gate can check/accept during a shared-IP training burst.
    if (url?.includes("/admin/") && now < localRateLimitUntil && !isAdminClientCooldownExempt(url)) {
      const retryAfterSeconds = Math.ceil((localRateLimitUntil - now) / 1000);
      return Promise.reject({
        message: "Rate limited: temporary client cooldown is active.",
        status: 429,
        response: {
          status: 429,
          data: {
            success: false,
            message: "Rate limited: temporary client cooldown is active.",
            retryAfter: retryAfterSeconds,
          },
        },
        isClientRateLimit: true,
      });
    }
    
    // Log login requests for debugging
    if (url?.includes('/auth/login')) {
      console.log(`Making login request to: ${method?.toUpperCase()} ${url} at ${new Date().toISOString()}`);
    }
    
    if (!hasPermission(url, method)) {
      return Promise.reject({
        message: "Permission Denied",
        status: 403,
        isClientPermissionDenied: true,
      });
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Database error detection patterns
const databaseErrorPatterns = [
  /buffering timed out/i,
  /mongodb/i,
  /database.*connection/i,
  /connection.*timeout/i,
  /server.*selection.*timeout/i,
  /socket.*timeout/i,
  /operation.*timed out/i,
  /failed to connect/i,
  /connection.*refused/i,
  /econnrefused/i,
  /enotfound/i,
];

// Global database error handler - can be set by components
let databaseErrorHandler: ((error: string) => void) | null = null;

export const setDatabaseErrorHandler = (handler: ((error: string) => void) | null) => {
  databaseErrorHandler = handler;
};

// Add response logging for debugging
axiosInstance.interceptors.response.use(
  (response) => {
    // Log successful login responses
    if (response.config.url?.includes('/auth/login')) {
      console.log(`Login request successful at ${new Date().toISOString()}`);
    }
    return response;
  },
  (error) => {
    if (error?.response?.status === 429) {
      const requestUrl = error?.config?.url as string | undefined;
      const isLoginRequest =
        typeof requestUrl === "string" && requestUrl.includes("/auth/login");
      let cooldownMs = getRetryAfterMs(error);
      if (!isLoginRequest) {
        cooldownMs = Math.min(cooldownMs, NON_LOGIN_RATE_LIMIT_BACKOFF_MAX_MS);
      }
      syncRateLimitUntil(Date.now() + cooldownMs);

      if (!hasLoggedRateLimit) {
        hasLoggedRateLimit = true;
        const loginPath = typeof error?.config?.url === "string" && error.config.url.includes("/auth/login");
        console.warn(
          loginPath
            ? `HTTP 429 (rate limited) on login. Client backoff ${Math.ceil(cooldownMs / 1000)}s; repeated failed attempts may hit hourly server limits.`
            : `API rate limit (429). Backing off admin-path requests for ${Math.ceil(cooldownMs / 1000)}s.`
        );
      }
    }

    // Log failed login responses
    if (error.config?.url?.includes('/auth/login')) {
      console.log(`Login request failed with status ${error.response?.status} at ${new Date().toISOString()}`);
    }
    
    // Check for database connection errors
    if (databaseErrorHandler) {
      const errorMessage = 
        error?.message || 
        error?.response?.data?.message || 
        error?.response?.data?.error ||
        error?.response?.data ||
        String(error);
      
      const errorString = typeof errorMessage === 'string' 
        ? errorMessage 
        : JSON.stringify(errorMessage);
      
      // Check for database error patterns
      const matchesPattern = databaseErrorPatterns.some(pattern => 
        pattern.test(errorString)
      );
      
      // Also check for 500 errors that might be database-related
      const isServerError = error?.response?.status >= 500 && error?.response?.status < 600;
      
      if (matchesPattern || (isServerError && errorString.toLowerCase().includes('timeout'))) {
        databaseErrorHandler(errorString);
      }
    }
    
    const status = error?.response?.status;
    const requestUrl = error?.config?.url as string | undefined;

    // Assumed-entity sessions carry the target user's token, so advisor-only pages left
    // in history keep getting refused. Tell the advisor to switch back instead of failing silently.
    if (
      status === 403 &&
      isPermissionDeniedResponse(error) &&
      isImpersonationSession() &&
      requestUrl?.includes("/admin/")
    ) {
      notifyImpersonationForbidden(requestUrl);
    }

    // Global session expiry handling for authenticated API calls
    if (
      shouldEndSessionForAuthError(error, requestUrl) &&
      !isAuthExemptRequest(requestUrl) &&
      !isOnLoginPage()
    ) {
      if (process.env.NODE_ENV === 'development') {
        console.info('[auth] session_expired_redirect', requestUrl, status);
      }
      endSessionAndRedirect(LOGIN_REASONS.session_expired);
    }
    
    return Promise.reject(error);
  }
);

function setAuthToken(token = "") {
  axiosInstance.defaults.headers.common.Authorization = `Bearer ${token}`;
}

const methods = {
  get: axiosInstance.get,
  post: axiosInstance.post,
  put: axiosInstance.put,
  delete: axiosInstance.delete,
  patch: axiosInstance.patch,
  setAuthToken,
};

export default methods;
